import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { translations, getStoredLanguage, getStoredLanguageOrDefault, setStoredLanguage } from '../translations';

/** Pagal šalies kodą parinkti kalbą: Vokietija/AT/Šveicarija ir kt. → de, Lietuva → lt, kita → en */
function getLanguageByCountryCode(countryCode) {
  if (!countryCode || typeof countryCode !== 'string') return 'en';
  const cc = countryCode.toUpperCase();
  if (cc === 'LT') return 'lt';
  if (['DE', 'AT', 'CH', 'LI', 'LU'].includes(cc)) return 'de';
  return 'en';
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(getStoredLanguage);
  const geoChecked = useRef(false);

  // Išsaugoti kalbą ir atnaujinti document.lang
  useEffect(() => {
    const L = language ?? 'lt';
    document.documentElement.lang = L === 'lt' ? 'lt' : L === 'de' ? 'de' : 'en';
    if (language !== null) setStoredLanguage(language);
  }, [language]);

  // Automatinis kalbos parinkimas pagal lokaciją (tik pirmą kartą, kai nėra išsaugotos kalbos)
  useEffect(() => {
    if (geoChecked.current) return;
    geoChecked.current = true;
    if (getStoredLanguage() !== null) return;

    fetch('https://ipapi.co/json/')
      .then((res) => res.json())
      .then((data) => {
        const lang = getLanguageByCountryCode(data.country_code);
        setLanguageState(lang);
        setStoredLanguage(lang);
      })
      .catch(() => {
        setLanguageState('en');
        setStoredLanguage('en');
      });
  }, []);

  const setLanguage = useCallback((lang) => {
    if (lang === 'lt' || lang === 'en' || lang === 'de') {
      setLanguageState(lang);
    }
  }, []);

  const effectiveLanguage = language ?? 'lt';

  const t = useCallback((key) => {
    const dict = translations[effectiveLanguage] || translations.lt;
    return dict[key] ?? translations.lt[key] ?? key;
  }, [effectiveLanguage]);

  const value = useMemo(
    () => ({ language: effectiveLanguage, setLanguage, t }),
    [effectiveLanguage, setLanguage, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    const fallbackT = (key) => translations.lt[key] ?? key;
    return {
      language: getStoredLanguageOrDefault(),
      setLanguage: () => {},
      t: fallbackT,
    };
  }
  return ctx;
}
